/**
 * Phase 166 — Scout Network Heatmap Tile
 *
 * Mounted inside the Phase 162 PlayerProgressionChart. Surfaces per-city
 * scout engagement aggregated from `/api/admin/scout/heatmap`. Top hub gets
 * a "🔥 N views" hero callout; remaining hubs rendered as horizontal bars
 * with view counts. Plus a compact referrer breakdown row underneath.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  MapPin, Flame, Globe2, Link as LinkIcon, RefreshCw, Hash, Loader2,
} from 'lucide-react';

const HUB_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#10b981', '#0ea5e9', '#a855f7', '#ec4899'];

export default function ScoutHeatmapTile({ jersey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    if (!jersey) { setData(null); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        jersey_number: jersey,
        days: String(days),
      });
      const r = await api.get(`/admin/scout/heatmap?${params.toString()}`);
      setData(r.data);
    } catch (_e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [jersey, days]);

  useEffect(() => { load(); }, [load]);

  if (!jersey) return null;

  const totalViews = data?.total_views ?? 0;
  const hubs = (data?.hubs || []).filter((h) => h.city && h.city !== 'Unknown');
  const referrers = data?.referrers || [];
  const topHub = hubs[0];
  const pendingGeo = data?.pending_geo_enrichment ?? 0;
  const maxViews = Math.max(1, ...hubs.map((h) => h.views));

  return (
    <Card className="border-rose-200/60 bg-gradient-to-br from-rose-50/30 via-white to-orange-50/20"
      data-testid="phase166-heatmap-tile">
      <CardContent className="p-4 sm:p-5 space-y-3.5">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-rose-500 to-orange-600 flex items-center justify-center flex-shrink-0">
              <Globe2 className="h-4.5 w-4.5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display font-semibold text-sm sm:text-base text-slate-800" data-testid="phase166-title">
                  Scout Network Heatmap
                </h3>
                <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[9px] font-bold uppercase tracking-wider">Phase 166</Badge>
                <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-[9px] font-bold uppercase tracking-wider">Legend</Badge>
              </div>
              <p className="text-[11px] text-muted-foreground" data-testid="phase166-jersey-label">
                <Hash className="inline h-2.5 w-2.5 -translate-y-px text-rose-500" />{' '}
                <span className="font-semibold">{jersey}</span> · last {days} days
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                data-testid={`phase166-days-${d}`}
                className={`text-[10px] font-bold px-2 py-1 rounded ${
                  days === d ? 'bg-rose-600 text-white' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                {d}d
              </button>
            ))}
            <Button
              variant="ghost" size="sm" onClick={load} disabled={loading}
              className="h-7 w-7 p-0"
              data-testid="phase166-refresh-btn"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            </Button>
          </div>
        </div>

        {/* Hero stat */}
        {topHub ? (
          <div
            className="rounded-xl px-3.5 py-3 border-2 flex items-start gap-3"
            style={{ borderColor: HUB_COLORS[0], background: `linear-gradient(135deg, ${HUB_COLORS[0]}14, white)` }}
            data-testid="phase166-hero-tile"
          >
            <Flame className="h-7 w-7 flex-shrink-0" style={{ color: HUB_COLORS[0] }} />
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">🔥 Hottest scouting hub</div>
              <div className="text-base sm:text-lg font-bold text-slate-900 leading-tight">
                {topHub.views} scout view{topHub.views === 1 ? '' : 's'} from{' '}
                <span style={{ color: HUB_COLORS[0] }}>{topHub.city}{topHub.country ? `, ${topHub.country}` : ''}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Last seen {topHub.last_viewed_at ? new Date(topHub.last_viewed_at).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) : '—'}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center" data-testid="phase166-empty">
            <MapPin className="h-5 w-5 text-slate-400 mx-auto mb-1" />
            <div className="text-[11px] text-slate-500">
              {totalViews > 0
                ? `${totalViews} view${totalViews === 1 ? '' : 's'} on file, but geolocation is still resolving${pendingGeo ? ` (${pendingGeo} pending)` : ''}.`
                : 'No scout views yet for this jersey. Share the link to start tracking traction.'}
            </div>
          </div>
        )}

        {/* Per-city bars */}
        {hubs.length > 0 && (
          <div className="space-y-1.5" data-testid="phase166-hubs-list">
            {hubs.slice(0, 6).map((h, idx) => (
              <div key={`${h.city}-${idx}`} className="flex items-center gap-2"
                data-testid={`phase166-hub-${h.city.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="w-24 sm:w-28 flex-shrink-0 text-[11px] font-semibold text-slate-700 truncate">
                  <span style={{ color: HUB_COLORS[idx % HUB_COLORS.length] }}>●</span> {h.city}
                  {h.country && <span className="text-slate-400 text-[10px] ml-0.5">·{h.country}</span>}
                </div>
                <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${(h.views / maxViews) * 100}%`,
                      background: HUB_COLORS[idx % HUB_COLORS.length],
                    }}
                  />
                </div>
                <div className="w-10 text-right text-[11px] font-bold text-slate-700 tabular-nums">
                  {h.views}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Referrer breakdown */}
        {referrers.length > 0 && (
          <div className="border-t border-slate-200 pt-2.5 space-y-1" data-testid="phase166-referrers">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
              <LinkIcon className="h-2.5 w-2.5" /> Referrers ({days}d)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {referrers.slice(0, 6).map((r, idx) => (
                <span key={`${r.source}-${idx}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-200 rounded-full text-[10px] font-semibold text-slate-700"
                  data-testid={`phase166-referrer-${idx}`}>
                  {r.source}
                  <span className="text-slate-400">·</span>
                  <span className="font-bold text-slate-800">{r.views}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Footer meta */}
        <div className="text-[10px] text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <span><strong className="text-slate-600">{totalViews}</strong> total views</span>
          <span>·</span>
          <span><strong className="text-slate-600">{data?.unique_cities ?? 0}</strong> unique cit{(data?.unique_cities ?? 0) === 1 ? 'y' : 'ies'}</span>
          {pendingGeo > 0 && (
            <>
              <span>·</span>
              <span className="text-amber-600"><strong>{pendingGeo}</strong> geo-pending</span>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
